import { Link } from "react-router-dom";
import { Anchor, Box, Group, Paper, Stack, Text, ThemeIcon, Title, UnstyledButton } from "@mantine/core";
import { BookOpen, ChevronRight } from "lucide-react";
import { GuideProgress } from "@nodus/ui";
import type { GuideSummary } from "@/lib/api/guides";
import { faqIcon } from "@/lib/faq-icons";
import { unfinishedGuides } from "@/lib/guides";
import { useMyGuides } from "@/hooks/use-guides";
import { useGuideLink } from "@/hooks/use-guide-modal";

/** How many guides the card lists before pointing at the Office Guide. */
export const GET_STARTED_LIMIT = 3;

/**
 * The dashboard's "Get started" card (#436): the member's unfinished guides
 * with their progress, most urgent first. Renders nothing when there is
 * nothing left to do (or the list can't be loaded) — the dashboard stays as it
 * was.
 */
export function GetStartedCardView({ guides, isCompanyAdmin }: { guides: GuideSummary[]; isCompanyAdmin: boolean }) {
  const guideLink = useGuideLink();
  const open = unfinishedGuides(guides, isCompanyAdmin);
  if (open.length === 0) return null;
  const shown = open.slice(0, GET_STARTED_LIMIT);
  const more = open.length - shown.length;

  return (
    <Paper withBorder p="lg" radius="md" component="section" aria-labelledby="get-started-title">
      <Stack gap="md">
        <Box>
          <Title order={4} id="get-started-title">
            Get started
          </Title>
          <Text fz="sm" c="dimmed">
            {open.length === 1 ? "1 setup guide is" : `${open.length} setup guides are`} waiting for you. Do one
            step at a time.
          </Text>
        </Box>
        <Stack gap="sm">
          {shown.map((g) => {
            const Icon = g.icon ? faqIcon(g.icon) : BookOpen;
            const action = g.done > 0 ? "Continue" : "Start";
            return (
              <UnstyledButton
                key={g.key}
                component={Link}
                {...guideLink(g.key)}
                aria-label={`${action}: ${g.title}`}
                p={6}
                m={-6}
                style={{ borderRadius: "var(--mantine-radius-md)" }}
              >
                <Group gap="md" wrap="nowrap" align="center">
                  <ThemeIcon variant="light" size={36} radius="md" style={{ flexShrink: 0 }}>
                    <Icon size={20} />
                  </ThemeIcon>
                  <Stack gap={4} flex={1} miw={0}>
                    <Text fw={600} fz="sm" truncate>
                      {g.title}
                    </Text>
                    <GuideProgress done={g.done} total={g.total} status={g.status} />
                  </Stack>
                  <Group
                    gap={2}
                    wrap="nowrap"
                    justify="flex-end"
                    w={{ base: 18, sm: 96 }}
                    c="var(--mantine-primary-color-filled)"
                    style={{ flexShrink: 0 }}
                  >
                    <Text fz="sm" fw={600} c="inherit" visibleFrom="sm">
                      {action}
                    </Text>
                    <ChevronRight size={18} />
                  </Group>
                </Group>
              </UnstyledButton>
            );
          })}
        </Stack>
        {more > 0 && (
          <Anchor component={Link} to="/faq" fz="sm">
            {more} more in the Office Guide
          </Anchor>
        )}
      </Stack>
    </Paper>
  );
}

export function GetStartedCard() {
  const { data } = useMyGuides();
  if (!data) return null;
  return <GetStartedCardView guides={data.guides} isCompanyAdmin={data.is_company_admin} />;
}
